import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { colors } from '../../../assets/styles/GlobalStyles';
import { Button } from '../../../components/ui/Button/Button';
import { FaUpload, FaCheck, FaArrowLeft, FaFilePdf, FaArrowUp } from 'react-icons/fa';
import { validatePDF, validateTotalSize, MAX_FILE_SIZE_MB } from '../../requerimentos/core/services/documentService';
import { destacarPendencias } from '../../../utils/destaqueCampos';

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${colors.darkBlue};
  margin-bottom: 1.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const InfoBox = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #475569;
`;

const InfoValue = styled.span`
  color: #1e293b;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
`;

const RequiredDocumentRow = styled.div`
  background: ${props => props.$destaque ? '#fef2f2' : '#fef3c7'};
  border: 1px solid ${props => props.$destaque ? '#dc2626' : '#fde68a'};
  scroll-margin-top: 6rem;
  transition: background 0.2s ease, border-color 0.2s ease;
  border-radius: 12px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DocumentLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  strong {
    color: #92400e;
    font-size: 1rem;
  }

  span {
    color: #78350f;
    font-size: 0.875rem;
  }
`;

const SelectFileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: ${colors.lightBlue};
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    border-color: ${colors.lightBlue};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #065f46;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;

  svg { flex-shrink: 0; }
`;

const OptionalSection = styled.div`
  margin: 1.5rem 0;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  margin-bottom: 1rem;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  span {
    color: ${colors.darkBlue};
    font-weight: 500;
  }
`;

const AdditionalUploadArea = styled.div`
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;

  &:hover {
    border-color: ${colors.lightBlue};
    background: #f1f5f9;
  }
`;

const FileList = styled.div`
  margin-top: 1rem;
`;

const FileItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  gap: 0.5rem;
`;

const FileName = styled.span`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #334155;
  min-width: 0;
  overflow-wrap: anywhere;

  svg { flex-shrink: 0; }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.25rem 0.5rem;
  flex-shrink: 0;

  &:hover {
    color: #dc2626;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

/**
 * @param {Object} protocoloData - Dados do protocolo (número, status)
 */
const DocumentUpload = ({ protocoloData, loading, onEnviar, onVoltar }) => {
  const [requerimentoFile, setRequerimentoFile] = useState(null);
  const [outrosArquivos, setOutrosArquivos] = useState([]);
  const [hasMaisDocumentos, setHasMaisDocumentos] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const fileInputRef = useRef(null);
  const additionalFileInputRef = useRef(null);

  const handleRequerimentoUpload = (event) => {
    const file = event.target.files[0];
    setErrorMessage(null);

    if (file) {
      const validation = validatePDF(file);
      if (!validation.isValid) {
        setErrorMessage(`❌ ${validation.error}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      const totalValidation = validateTotalSize([file, ...outrosArquivos]);
      if (!totalValidation.isValid) {
        setErrorMessage(`❌ ${totalValidation.error}`);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setRequerimentoFile(file);
    }
  };

  const handleAdditionalUpload = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      const validation = validatePDF(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(`${file.name} - ${validation.error}`);
      }
    });

    const totalValidation = validateTotalSize([requerimentoFile, ...outrosArquivos, ...validFiles]);
    if (!totalValidation.isValid) {
      setErrorMessage(`❌ ${totalValidation.error}`);
      if (additionalFileInputRef.current) {
        additionalFileInputRef.current.value = '';
      }
      return;
    }

    if (invalidFiles.length > 0) {
      setErrorMessage(`❌ Arquivos rejeitados:\n${invalidFiles.join('\n')}`);
    } else {
      setErrorMessage(null);
    }

    setOutrosArquivos([...outrosArquivos, ...validFiles]);
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.value = '';
    }
  };

  const handleRemoverArquivo = (index) => {
    const novosArquivos = [...outrosArquivos];
    novosArquivos.splice(index, 1);
    setOutrosArquivos(novosArquivos);
  };

  // Desmarcar "Anexar mais documentos" descarta os adicionais já escolhidos —
  // antes eles só sumiam da tela e continuavam indo no envio.
  const handleToggleMaisDocumentos = (event) => {
    const marcado = event.target.checked;
    setHasMaisDocumentos(marcado);
    if (!marcado) {
      setOutrosArquivos([]);
      if (additionalFileInputRef.current) {
        additionalFileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = () => {
    if (!requerimentoFile) {
      setTentouEnviar(true);
      destacarPendencias(['requerimento']);
      return;
    }
    // errorMessage só descreve arquivos recusados (que nem entraram na lista),
    // então não bloqueia o envio do que já está anexado.
    onEnviar(requerimentoFile, outrosArquivos);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(2)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <Card>
      <CardTitle>
        <FaUpload style={{ marginRight: '0.5rem', color: colors.lightBlue }} />
        Anexar Documentos
      </CardTitle>

      <InfoBox>
        <InfoRow>
          <InfoLabel>Nº do Protocolo:</InfoLabel>
          <InfoValue>{protocoloData?.numeroProtocolo || 'A definir'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Status:</InfoLabel>
          <InfoValue style={{ color: '#eab308' }}>⚠️ Requerimento criado - anexe os documentos para concluir o protocolo</InfoValue>
        </InfoRow>
      </InfoBox>

      <div style={{
        background: '#e0f2fe',
        color: '#0369a1',
        padding: '0.5rem 1rem',
        borderRadius: '8px',
        marginBottom: '1rem',
        fontSize: '0.75rem',
        textAlign: 'center'
      }}>
        📄 Apenas arquivos PDF - Máximo de {MAX_FILE_SIZE_MB}MB somando todos os arquivos
      </div>

      {errorMessage && (
        <div role="alert" style={{
          overflowWrap: 'anywhere',
          background: '#fee2e2',
          color: '#dc2626',
          padding: '0.75rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.875rem',
          whiteSpace: 'pre-line'
        }}>
          {errorMessage}
        </div>
      )}

      <RequiredDocumentRow data-field-name="requerimento" $destaque={tentouEnviar && !requerimentoFile}>
        <DocumentLabel>
          <strong>📄 Documento obrigatório:</strong>
          <span>Requerimento (PDF)</span>
        </DocumentLabel>
        {requerimentoFile ? (
          <FileInfo>
            <FaFilePdf color="#ef4444" />
            {requerimentoFile.name} ({formatFileSize(requerimentoFile.size)})
            <RemoveButton onClick={() => setRequerimentoFile(null)}>✕</RemoveButton>
          </FileInfo>
        ) : (
          <>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleRequerimentoUpload}
              accept=".pdf,application/pdf"
            />
            <SelectFileButton onClick={() => fileInputRef.current?.click()}>
              <FaArrowUp size={12} />
              Selecionar arquivo PDF
            </SelectFileButton>
          </>
        )}
        {tentouEnviar && !requerimentoFile && (
          <small role="alert" style={{ flexBasis: '100%', color: '#dc2626', fontSize: '0.8rem' }}>
            Anexe o requerimento em PDF para enviar o protocolo
          </small>
        )}
      </RequiredDocumentRow>

      <OptionalSection>
        <CheckboxLabel>
          <input
            type="checkbox"
            checked={hasMaisDocumentos}
            onChange={handleToggleMaisDocumentos}
          />
          <span>📎 Anexar mais documentos (opcional - apenas PDF)</span>
        </CheckboxLabel>

        {hasMaisDocumentos && (
          <>
            <AdditionalUploadArea onClick={() => additionalFileInputRef.current?.click()}>
              <FaUpload size={24} style={{ color: colors.lightBlue, marginBottom: '0.5rem' }} />
              <p style={{ margin: 0, fontWeight: 500 }}>Clique para adicionar mais arquivos PDF</p>
              <p style={{ fontSize: '0.75rem', color: colors.textMuted, marginTop: '0.5rem' }}>
                PDF apenas - Máximo {MAX_FILE_SIZE_MB}MB somando todos os arquivos
              </p>
            </AdditionalUploadArea>

            <input
              ref={additionalFileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleAdditionalUpload}
              accept=".pdf,application/pdf"
            />

            {outrosArquivos.length > 0 && (
              <FileList>
                <h4 style={{ marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                  Documentos adicionais ({outrosArquivos.length}):
                </h4>
                {outrosArquivos.map((arquivo, index) => (
                  <FileItem key={index}>
                    <FileName>
                      <FaFilePdf color="#ef4444" />
                      {arquivo.name} ({formatFileSize(arquivo.size)})
                    </FileName>
                    <RemoveButton onClick={() => handleRemoverArquivo(index)}>✕</RemoveButton>
                  </FileItem>
                ))}
              </FileList>
            )}
          </>
        )}
      </OptionalSection>

      <ButtonGroup>
        <Button
          variant="secondary"
          onClick={onVoltar}
          disabled={loading}
          leftIcon={<FaArrowLeft />}
        >
          Voltar
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
          leftIcon={<FaCheck />}
        >
          {loading ? 'Enviando...' : 'Enviar Protocolo'}
        </Button>
      </ButtonGroup>
    </Card>
  );
};

export default DocumentUpload;
